  <!-- Checklist Header -->
  <div class="glass-card border border-light border-1 rounded-lg mb-3 p-3 shadow">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <p class="text-x fw-bold mb-0">Compliance Checklist</p>
      <div class="d-flex align-items-center gap-2">
        <span class="status-badge text-xs" :class="completion_percentage === 100 ? 'manually-verified' : 'pending'">
          {{ completion_percentage }}% Complete
        </span>

        <!-- Re-run checks button (visible only when 100% complete) -->
        <button
          v-if="completion_percentage === 100"
          class="btn btn-primary-dark btn-sm rounded text-xxs"
          :disabled="is_rerunning_checks"
          @click="re_run_checks"
        >
          <span v-if="is_rerunning_checks" class="spinner-border spinner-border-sm text-white me-1"></span>
          Re-run checks
        </button>
      </div>
    </div>

    <!-- Progress Bar -->
    <div class="progress-bar-container">
      <div class="progress-bar-fill" :style="progress_bar_style"></div>
    </div>
    <div class="text-xxs fw-bold mt-2 text-end">
      {{ completed_count }}/{{ checklist_items.length }} sections completed
    </div>
  </div>

  <!-- Checklist Items -->
  <div class="mt-3">
    <div v-if="is_loading" class="text-center py-4">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
    <p class="mt-2">Loading compliance checklist...</p>
    </div>
    <div v-else-if="checklist_items.length === 0" class="text-center py-4 bg-transparent">
      <p>No checklist items found.</p>
    </div>
    <div v-else v-for="(item, index) in checklist_items" :key="item.id" class="mb-2">
      <div class="card shadow border-0 rounded-lg p-3">
        <!-- Section Header -->
        <div class="text-xs fw-bold border-bottom border-2 pb-2 mb-3">
          <!-- Top Row: Title and Status -->
          <div class="d-flex justify-content-between align-items-center mb-3" @click="toggle_section(index)" style="cursor: pointer;">
            <!-- Left: Title with icon -->
            <div class="d-flex align-items-center gap-2">
              <span :class="item.is_completed ? 'text-success' : 'text-danger'" class="fs-4">
                <i :class="item.is_completed ? 'bi bi-check-circle' : 'bi bi-exclamation-circle'"></i>
              </span>
              <span class="text-capitalize text-x fw-bold">{{ item.title }}</span>
            </div>

            <!-- Right: Badge and chevron -->
            <div class="d-flex align-items-center gap-3">
              <span class="status-badge text-xs" :class="item.is_completed ? 'manually-verified' : 'pending'">
                {{ item.is_completed ? 'Completed' : 'Pending' }}
              </span>
              <i :class="item.is_open ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
            </div>
          </div>

          <!-- Requirements Status Section (always visible) -->
          <div v-if="item.requirement_statuses && item.requirement_statuses.length > 0" class="mt-3 pt-2 border-top">
            <div class="p-2 rounded bg-light">
              <div class="d-flex align-items-center gap-2 mb-2">
                <span class="text-xs text-dark">
                  {{ item.requirement_statuses.filter(r => r.is_met).length }} of {{ item.requirement_statuses.length }} requirements met
                </span>
              </div>
              
              <!-- Quick preview badges -->
              <div class="d-flex flex-wrap gap-1">
                <span
                  v-for="(req_status, idx) in item.requirement_statuses.filter(r => r.is_met)"
                  :key="'met-' + idx"
                  class="badge border border-success text-success text-xxs fw-normal px-2 py-1"
                >
                  <i class="bi bi-check-circle-fill me-1"></i>

                  {{ req_status.type }}
                  <template v-if="req_status.verification_type && req_status.verification_type !== 'MANUAL'">
                    :{{ req_status.verification_type }}
                  </template>
                </span>

                <span
                  v-for="(req_status, idx) in item.requirement_statuses.filter(r => !r.is_met)"
                  :key="'unmet-' + idx"
                  class="badge border border-danger text-danger text-xxs fw-normal px-2 py-1"
                >
                  <i class="bi bi-x-circle-fill me-1"></i>

                  {{ req_status.type }}
                  <template v-if="req_status.verification_type && req_status.verification_type !== 'MANUAL'">
                    : {{ req_status.verification_type }}
                  </template>
                </span>
              </div>

            </div>
          </div>
        </div>
        <!-- Target Questions -->
        <div v-if="item.is_open">
          <div class="mb-2 d-flex align-items-center">
            <span class="text-x text-danger me-1">*</span>
            <span class="text-xs">Required Questions :</span>

            <span class="text-xs text-muted ms-1">
              {{ get_answered_count(item.target_questions) }} / {{ item.target_questions.length }}
            </span>
          </div>


          <div v-for="(question, q_index) in get_applicable_questions(item.target_questions)" :key="question.id" class="mb-2 ms-2">
            <div class="d-flex justify-content-between align-items-start">
              <div class="d-flex flex-grow-1 align-items-start">
                <span :class="question.is_answered ? 'text-success' : 'text-muted'" class="me-2 fs-6">
                  <i :class="question.is_answered ? 'bi bi-check-circle-fill' : 'bi bi-circle'"
                  ></i>
                </span>
                <div class="flex-grow-1">
                  <label class="text-dark text-xs text-capitalize fw-7 mb-1">
                    {{ q_index + 1 }}. {{ question.text }}
                  </label>
                  <!-- Answer display -->
                  <div v-if="question.is_answered">
                    <!-- FILE TYPE -->
                    <div
                      v-if="question.question_type === 'file'"
                      class="d-flex align-items-center justify-content-between flex-wrap mt-3 ms-2"
                    >
                      <!-- Left: file name -->
                      <span class="text-xxs text-muted fw-6 text-truncate flex-grow-1 me-3">
                        {{ get_file_name_from_url(question.answer) }}
                      </span>

                      <!-- Right: preview icon -->
                      <div class="d-flex align-items-center gap-3 flex-shrink-0">
                        <a
                          href="#"
                          class="text-dark text-decoration-none"
                          @click.prevent.stop="preview_file(question.answer)"
                          title="Preview"
                        >
                          <i class="bi bi-eye"></i>
                        </a>
                      </div>
                    </div>


                    <!-- NON-FILE TYPES -->
                    <div
                      v-else
                      class="text-xxs text-muted fst-italic"
                    >
                      Answer: {{ format_answer(question.answer) }}
                    </div>

                  </div>

                  <div v-else class="text-xxs text-danger">
                    No answer provided
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Submit Evaluation Button -->
          <div v-if="item.is_completed" class="mt-3 ms-2">
            <button
              v-if="!item.is_evaluated"
              class="btn btn-primary-dark btn-sm rounded text-xxs shadow"
              @click="submit_evaluation_result(item.association_id, true)"
            >
              Mark as Evaluated
            </button>

            <button
              v-else
              class="btn btn-success btn-sm rounded text-xxs shadow"
              disabled
            >
              Evaluated
            </button>
          </div>

        </div>
      </div>
    </div>
  </div>
  
<div v-if="preview_modal" class="custom-modal-overlay" @click.self="preview_modal = false">
  <FilePreviewComponent
    :presigned_url="preview_url"
    :file_name="preview_file_name"
    @close="close_the_file_preview"
    :key="preview_url"
  />
</div>